import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  adminApi,
  type LabelFeedbackListItem,
  type Tenant,
} from '../../api/client';
import { useToast } from '../../contexts/ToastContext';
import { SkeletonTable } from '../../components/SkeletonTable';
import { EmptyState } from '../../components/EmptyState';
import clsx from 'clsx';

type FeedbackStatus = 'new' | 'reviewed' | 'resolved';

function severityBadge(sev?: string) {
  const s = (sev || '').toLowerCase();
  if (s === 'critical' || s === 'high')
    return 'bg-red-100 text-red-800 border-red-200';
  if (s === 'medium') return 'bg-amber-100 text-amber-900 border-amber-200';
  if (s === 'low') return 'bg-gray-100 text-gray-700 border-gray-200';
  return 'bg-slate-50 text-slate-600 border-slate-200';
}

function statusBadge(status: string) {
  if (status === 'new') return 'bg-blue-100 text-blue-800 border-blue-200';
  if (status === 'reviewed') return 'bg-violet-100 text-violet-800 border-violet-200';
  if (status === 'resolved') return 'bg-green-100 text-green-800 border-green-200';
  return 'bg-gray-100 text-gray-700 border-gray-200';
}

function notifyBadge(n?: string) {
  if (n === 'ok') return 'bg-green-50 text-green-800 border-green-200';
  if (n === 'partial') return 'bg-amber-50 text-amber-900 border-amber-200';
  if (n === 'failed') return 'bg-red-50 text-red-800 border-red-200';
  if (n === 'skipped') return 'bg-gray-50 text-gray-600 border-gray-200';
  return 'bg-slate-50 text-slate-500 border-slate-200';
}

function shortComment(text?: string, max = 80): string {
  if (!text?.trim()) return '—';
  const t = text.trim().replace(/\s+/g, ' ');
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}

function formatWhen(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function AdminLabelFeedbackPage() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const [tenantId, setTenantId] = useState('');
  const [status, setStatus] = useState<'' | FeedbackStatus>('');
  const [severity, setSeverity] = useState('');
  const [source, setSource] = useState('');
  const [templateKey, setTemplateKey] = useState('');
  const [hasGithubIssue, setHasGithubIssue] = useState<'' | 'true' | 'false'>('');
  const [selected, setSelected] = useState<LabelFeedbackListItem | null>(null);

  const { data: tenants } = useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: () => adminApi.getTenants(),
  });

  const tenantNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of tenants ?? []) {
      map.set(t.tenantId, t.name || t.tenantId);
    }
    return map;
  }, [tenants]);

  const filters = useMemo(
    () => ({
      tenantId: tenantId || undefined,
      status: status || undefined,
      severity: severity || undefined,
      source: source || undefined,
      templateKey: templateKey.trim() || undefined,
      hasGithubIssue:
        hasGithubIssue === 'true'
          ? true
          : hasGithubIssue === 'false'
            ? false
            : undefined,
      limit: 200,
    }),
    [tenantId, status, severity, source, templateKey, hasGithubIssue],
  );

  const { data, isLoading, isFetching, refetch, error } = useQuery({
    queryKey: ['admin', 'label-feedback', filters],
    queryFn: () => adminApi.listLabelFeedback(filters),
  });

  const statusMutation = useMutation({
    mutationFn: ({
      item,
      next,
    }: {
      item: LabelFeedbackListItem;
      next: FeedbackStatus;
    }) => adminApi.updateLabelFeedbackStatus(item.tenantId, item.feedbackId, next),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'label-feedback'] });
      setSelected((prev) =>
        prev && prev.feedbackId === updated.feedbackId ? updated : prev,
      );
      addToast({ type: 'success', message: `Marked as ${updated.status}` });
    },
    onError: (err) => {
      addToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to update status',
      });
    },
  });

  const items = data?.items ?? [];
  const newCount = items.filter((i) => i.status === 'new').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Label feedback</h1>
          <p className="text-sm text-gray-600 mt-1">
            Appearance feedback from Test Label, MCP, and API — stored in{' '}
            <code className="text-xs bg-gray-100 px-1 rounded">labelfeedback</code>.
            GitHub issues open only for medium+ severity (or hot pin categories).
          </p>
        </div>
        <button
          type="button"
          className="btn btn-secondary text-sm"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          {isFetching ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* Filters */}
      <div className="card p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
          <div>
            <label className="label text-xs">Client (tenant)</label>
            <select
              className="input text-sm"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
            >
              <option value="">All clients</option>
              {(tenants ?? []).map((t: Tenant) => (
                <option key={t.tenantId} value={t.tenantId}>
                  {t.name} ({t.tenantId})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label text-xs">Status</label>
            <select
              className="input text-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value as '' | FeedbackStatus)}
            >
              <option value="">All</option>
              <option value="new">New</option>
              <option value="reviewed">Reviewed</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>
          <div>
            <label className="label text-xs">Severity</label>
            <select
              className="input text-sm"
              value={severity}
              onChange={(e) => setSeverity(e.target.value)}
            >
              <option value="">All</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
          <div>
            <label className="label text-xs">Source</label>
            <select
              className="input text-sm"
              value={source}
              onChange={(e) => setSource(e.target.value)}
            >
              <option value="">All</option>
              <option value="portal">Portal</option>
              <option value="mcp">MCP</option>
              <option value="api">API</option>
            </select>
          </div>
          <div>
            <label className="label text-xs">GitHub issue</label>
            <select
              className="input text-sm"
              value={hasGithubIssue}
              onChange={(e) =>
                setHasGithubIssue(e.target.value as '' | 'true' | 'false')
              }
            >
              <option value="">Any</option>
              <option value="true">Has issue</option>
              <option value="false">No issue</option>
            </select>
          </div>
          <div>
            <label className="label text-xs">Template contains</label>
            <input
              className="input text-sm"
              placeholder="e.g. home-depot"
              value={templateKey}
              onChange={(e) => setTemplateKey(e.target.value)}
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-3 text-xs text-gray-600">
          <span>
            Showing <strong>{items.length}</strong>
            {data?.count != null ? ` of ${data.count}` : ''} row(s)
          </span>
          {newCount > 0 && (
            <span className="text-blue-700 font-medium">{newCount} new</span>
          )}
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
          {error instanceof Error ? error.message : 'Failed to load feedback'}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        {/* Table */}
        <div className="xl:col-span-3 card overflow-hidden">
          {isLoading ? (
            <div className="p-4">
              <SkeletonTable rows={8} columns={6} />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              title="No feedback yet"
              description="Submissions from Test Label and MCP will appear here."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b text-left text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">When</th>
                    <th className="px-3 py-2 font-medium">Client</th>
                    <th className="px-3 py-2 font-medium">Template</th>
                    <th className="px-3 py-2 font-medium">Sev</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium">Notify</th>
                    <th className="px-3 py-2 font-medium">Comment</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map((item) => {
                    const isSel = selected?.feedbackId === item.feedbackId;
                    return (
                      <tr
                        key={`${item.tenantId}-${item.feedbackId}`}
                        className={clsx(
                          'cursor-pointer hover:bg-primary-50/40 transition-colors',
                          isSel && 'bg-primary-50',
                        )}
                        onClick={() => setSelected(item)}
                      >
                        <td className="px-3 py-2 whitespace-nowrap text-gray-600 text-xs">
                          {formatWhen(item.createdAt)}
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-900 truncate max-w-[10rem]">
                            {tenantNameById.get(item.tenantId) || item.tenantId}
                          </div>
                          <div className="text-[11px] text-gray-400 truncate max-w-[10rem]">
                            {item.tenantId}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="truncate max-w-[12rem] text-gray-800 font-mono text-xs">
                            {item.templateKey}
                          </div>
                          <div className="text-[11px] text-gray-400">
                            {item.source}
                            {item.annotationCount
                              ? ` · ${item.annotationCount} pin(s)`
                              : ''}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={clsx(
                              'inline-flex px-1.5 py-0.5 rounded border text-[11px] font-medium capitalize',
                              severityBadge(item.severity),
                            )}
                          >
                            {item.severity || '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={clsx(
                              'inline-flex px-1.5 py-0.5 rounded border text-[11px] font-medium capitalize',
                              statusBadge(item.status),
                            )}
                          >
                            {item.status}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={clsx(
                              'inline-flex px-1.5 py-0.5 rounded border text-[11px] font-medium',
                              notifyBadge(item.notifyStatus),
                            )}
                          >
                            {item.notifyStatus || '—'}
                          </span>
                          {item.githubIssueNumber != null && (
                            <div className="text-[11px] text-primary-600 mt-0.5">
                              #{item.githubIssueNumber}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-700 max-w-[14rem]">
                          {shortComment(item.overallComment)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Detail panel */}
        <div className="xl:col-span-2 card p-4 space-y-4 min-h-[20rem]">
          {!selected ? (
            <div className="text-sm text-gray-500 py-12 text-center">
              Select a row to see full details, annotations, and triage actions.
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Detail</h2>
                  <p className="text-xs text-gray-500 font-mono break-all">
                    {selected.feedbackId}
                  </p>
                </div>
                <div className="flex flex-wrap gap-1">
                  {(['new', 'reviewed', 'resolved'] as FeedbackStatus[]).map(
                    (s) => (
                      <button
                        key={s}
                        type="button"
                        className={clsx(
                          'text-xs px-2 py-1 rounded border capitalize',
                          selected.status === s
                            ? 'bg-primary-600 text-white border-primary-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50',
                        )}
                        disabled={
                          statusMutation.isPending || selected.status === s
                        }
                        onClick={() =>
                          statusMutation.mutate({ item: selected, next: s })
                        }
                      >
                        {s}
                      </button>
                    ),
                  )}
                </div>
              </div>

              <dl className="grid grid-cols-1 gap-2 text-sm">
                <div className="flex justify-between gap-2 border-b border-gray-100 pb-1">
                  <dt className="text-gray-500">Client</dt>
                  <dd className="text-right font-medium text-gray-900">
                    {tenantNameById.get(selected.tenantId) || selected.tenantId}
                    <div className="text-[11px] font-normal text-gray-400">
                      {selected.tenantId}
                    </div>
                  </dd>
                </div>
                <div className="flex justify-between gap-2 border-b border-gray-100 pb-1">
                  <dt className="text-gray-500">Template</dt>
                  <dd className="text-right font-mono text-xs text-gray-800 break-all">
                    {selected.templateKey}
                    {selected.templateVersion
                      ? ` @ ${selected.templateVersion}`
                      : ''}
                  </dd>
                </div>
                <div className="flex justify-between gap-2 border-b border-gray-100 pb-1">
                  <dt className="text-gray-500">When</dt>
                  <dd className="text-right text-gray-800">
                    {formatWhen(selected.createdAt)}
                  </dd>
                </div>
                <div className="flex justify-between gap-2 border-b border-gray-100 pb-1">
                  <dt className="text-gray-500">Source</dt>
                  <dd className="text-right capitalize text-gray-800">
                    {selected.source}
                    {selected.createdBy ? ` · ${selected.createdBy}` : ''}
                  </dd>
                </div>
                <div className="flex justify-between gap-2 border-b border-gray-100 pb-1">
                  <dt className="text-gray-500">Severity</dt>
                  <dd>
                    <span
                      className={clsx(
                        'inline-flex px-1.5 py-0.5 rounded border text-xs font-medium capitalize',
                        severityBadge(selected.severity),
                      )}
                    >
                      {selected.severity || 'none'}
                    </span>
                  </dd>
                </div>
                <div className="flex justify-between gap-2 border-b border-gray-100 pb-1">
                  <dt className="text-gray-500">Notify</dt>
                  <dd className="text-right text-xs">
                    <span
                      className={clsx(
                        'inline-flex px-1.5 py-0.5 rounded border font-medium',
                        notifyBadge(selected.notifyStatus),
                      )}
                    >
                      {selected.notifyStatus || '—'}
                    </span>
                    {selected.notifyError && (
                      <div className="text-amber-700 mt-1 max-w-[16rem] ml-auto">
                        {selected.notifyError}
                      </div>
                    )}
                  </dd>
                </div>
                {selected.githubIssueUrl && (
                  <div className="flex justify-between gap-2 border-b border-gray-100 pb-1">
                    <dt className="text-gray-500">GitHub</dt>
                    <dd className="text-right">
                      <a
                        href={selected.githubIssueUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary-600 hover:underline text-sm"
                      >
                        #{selected.githubIssueNumber ?? 'issue'}
                      </a>
                    </dd>
                  </div>
                )}
              </dl>

              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                  Overall comment
                </h3>
                <p className="text-sm text-gray-800 whitespace-pre-wrap bg-gray-50 border border-gray-100 rounded-lg p-3">
                  {selected.overallComment?.trim() || '_(empty)_'}
                </p>
              </div>

              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                  Annotations ({selected.annotations?.length ?? 0})
                </h3>
                {!selected.annotations?.length ? (
                  <p className="text-sm text-gray-500">No zone/field pins.</p>
                ) : (
                  <ul className="space-y-2 max-h-64 overflow-y-auto">
                    {selected.annotations.map((a, idx) => {
                      const zoneId = a.zoneId != null ? String(a.zoneId) : '';
                      const fieldName =
                        a.fieldName != null ? String(a.fieldName) : '';
                      const cat =
                        (a.issueCategory as string) ||
                        (a.category as string) ||
                        '';
                      const comment = (a.comment as string) || '';
                      return (
                        <li
                          key={idx}
                          className="text-xs border border-gray-200 rounded-lg p-2 bg-white"
                        >
                          <div className="font-medium text-gray-800">
                            {fieldName
                              ? `Field ${fieldName}`
                              : zoneId
                                ? `Zone ${zoneId}`
                                : (a.targetType as string) || `Pin ${idx + 1}`}
                            {cat ? (
                              <span className="ml-1 font-normal text-gray-500">
                                · {cat}
                              </span>
                            ) : null}
                          </div>
                          {comment && (
                            <div className="text-gray-700 mt-0.5">{comment}</div>
                          )}
                          {a.xNorm != null && (
                            <div className="text-gray-400 mt-0.5 font-mono">
                              x={Number(a.xNorm).toFixed(2)} y=
                              {Number(a.yNorm ?? 0).toFixed(2)}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
